import { ProductItem } from '@/lib/types/api';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ButtonGhost } from '../ui/Buttons/ButtonGhost/ButtonGhost';
import Drawer from '../ui/Modals/Drawer/Drawer';
import { Input } from './Input';
import { InputTerms } from './InputTerms';
import { SuccessCheck } from './Success/SuccessCheck';
import { FaDownload } from 'react-icons/fa';
import axios from 'axios';
import { useRouter } from 'next/router';
import useUserMetadata from '@/lib/hooks/useUserData';
import { useUser } from '@auth0/nextjs-auth0/client';

function DownloadPDF({ product }: { product: ProductItem }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('products');
  const { locale } = useRouter();

  type FormInputs = {
    'counter-offer_first-name': string;
    'counter-offer_last-name': string;
    'counter-offer_city': string;
    'counter-offer_email': string;
    'counter-offer_company': string;
    'counter-offer_phone-prefix': string;
    'counter-offer_phone': string;
    'counter-offer_country': string;
    'counter-offer_terms': boolean;
    'counter-offer_price': string;
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    reset,
  } = useForm<FormInputs>({
    mode: 'onBlur',
  });

  async function submit(data: FormInputs) {
    console.log(data);
    await axios.post('/api2/download-pdf-requests', {
      company: data['counter-offer_company'],
      email: data['counter-offer_email'],
      first_name: data['counter-offer_first-name'],
      last_name: data['counter-offer_last-name'],
      language_code: locale,
      product_id: product.id,
    });
    try {
      window.print();
    } catch (error) {
      console.log(error);
    }
  }

  const { metadata } = useUserMetadata();
  const { user } = useUser();
  useEffect(() => {
    function handleReset() {
      reset({
        'counter-offer_first-name': metadata?.firstName,
        'counter-offer_last-name': metadata?.lastName,
        'counter-offer_company': metadata?.companyName,
        'counter-offer_email': user?.email ?? metadata?.companyEmail,
      });
    }
    // run handleReset after one sec
    const timer = setTimeout(() => handleReset(), 1000);
    return () => clearTimeout(timer);
  }, [metadata, reset, isOpen, user?.email]);

  return (
    <div className='no-print'>
      <div
        onClick={() => setIsOpen(true)}
        data-testid='btn-open-download-pdf-form'
      >
        <ButtonGhost hasOnlyBottomBorder>
          <FaDownload className='mr-2' />
          {t('products:DOWNLOAD_PDF')}
        </ButtonGhost>
      </div>
      <Drawer hasLogo isOpen={isOpen} setIsOpen={setIsOpen} size='full'>
        <hr />
        <div
          data-testid='counter-offer-form'
          className='flex min-h-[80vh] items-center justify-center'
        >
          {!isSubmitSuccessful ? (
            <form
              className='mx-auto max-w-xl'
              onSubmit={handleSubmit(submit)}
              data-testid='get-counter-offer_form'
            >
              <h1 className='my-6 text-center'>
                {t('products:PRINT_PDF_FORM_HEADER')}
              </h1>
              <p className='mb-6 text-center'>
                {t('products:PRINT_PDF_FORM_SUBHEADER')}
              </p>
              <div className='space-y-2'>
                <div className='flex space-x-2'>
                  <div className='w-1/2'>
                    <Input<FormInputs>
                      name='counter-offer_first-name'
                      register={register}
                      errors={errors}
                      placeholder={t('products:FORM_PLACEHOLDER_FIRSTNAME')}
                      validation={{
                        required: true,
                        maxLength: 50,
                      }}
                    />
                  </div>
                  <div className='w-1/2'>
                    <Input<FormInputs>
                      name='counter-offer_last-name'
                      register={register}
                      errors={errors}
                      placeholder={t('FORM_PLACEHOLDER_LASTNAME')}
                      validation={{
                        required: true,
                        maxLength: 50,
                      }}
                    />
                  </div>
                </div>
                <Input<FormInputs>
                  name='counter-offer_company'
                  register={register}
                  errors={errors}
                  placeholder={t('FORM_PLACEHOLDER_COMPANY')}
                  validation={{
                    required: true,
                    maxLength: 100,
                  }}
                />
                <Input<FormInputs>
                  type='email'
                  name='counter-offer_email'
                  register={register}
                  errors={errors}
                  placeholder={t('FORM_PLACEHOLDER_EMAIL')}
                  validation={{
                    required: true,
                    maxLength: 100,
                  }}
                />
                <InputTerms<FormInputs>
                  name='counter-offer_terms'
                  register={register}
                  errors={errors}
                />
              </div>
              <div className='flex w-full items-center justify-center lg:justify-end'>
                <button
                  type='submit'
                  className='mx-auto mb-12 mt-4 w-full max-w-xs rounded bg-brand-500 px-8 py-4 text-center uppercase text-white hover:bg-brand-400'
                  disabled={isSubmitting}
                >
                  {t('products:PRINT_PDF_GET_PDF')}
                </button>
              </div>
            </form>
          ) : (
            <SuccessCheck />
          )}
        </div>
      </Drawer>
    </div>
  );
}

export function FormTitle({ title }: { title: string }) {
  return (
    <div>
      <p className='text-center text-muted-500'>{title}</p>
    </div>
  );
}

export default DownloadPDF;
